// Recent searches for the dropdown shown before anything is typed
// (sections/search-suggestions.liquid). Kept in the visitor's browser only;
// every visit to the search page counts, however it was reached.
const RECENT_SEARCHES_KEY = 'magecraft-recent-searches';
const RECENT_SEARCHES_LIMIT = 5;

function getRecentSearches() {
  try {
    const searches = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY));
    return Array.isArray(searches) ? searches : [];
  } catch (error) {
    return [];
  }
}

function setRecentSearches(searches) {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
  } catch (error) {
    // Storage blocked or full: the list just isn't kept.
  }
}

if (window.location.pathname === routes.search_url) {
  const term = new URLSearchParams(window.location.search).get('q')?.trim();
  if (term) {
    const others = getRecentSearches().filter((search) => search.toLowerCase() !== term.toLowerCase());
    setRecentSearches([term, ...others].slice(0, RECENT_SEARCHES_LIMIT));
  }
}

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      if (document.activeElement === this.input) this.showSuggestions();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.showSuggestions();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showSuggestions();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    // Leave the suggestions open while moving through them with the arrow keys
    if (!this.getQuery().length && !this.hasAttribute('suggestions')) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Up to 8 of each type, instead of 10 shared between products and categories.
    const resources = 'resources[limit]=8&resources[limit_scope]=each';

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${resources}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  // Recent searches, popular categories and bestsellers, shown while the field
  // is focused and empty. Loaded once per page and shared by both search
  // fields (desktop header and mobile), like the cached results.
  showSuggestions() {
    // First load on this page: show the same spinner as while results load
    if (!PredictiveSearch.suggestionsMarkup) {
      this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.setLiveRegionLoadingState();
    }

    if (!PredictiveSearch.suggestionsRequest) {
      const url = `${routes.all_products_collection_url}?sort_by=best-selling&section_id=search-suggestions`;
      PredictiveSearch.suggestionsRequest = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          PredictiveSearch.suggestionsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-search-suggestions').innerHTML;
          return PredictiveSearch.suggestionsMarkup;
        })
        .catch((error) => {
          PredictiveSearch.suggestionsRequest = null;
          throw error;
        });
    }

    PredictiveSearch.suggestionsRequest
      .then((markup) => {
        // The visitor may have typed or left the field in the meantime
        if (document.activeElement !== this.input || this.getQuery().length) return;
        this.renderSuggestions(markup);
      })
      .catch((error) => {
        this.removeAttribute('loading');
        console.error(error);
      });
  }

  renderSuggestions(markup) {
    this.removeAttribute('loading');
    this.predictiveSearchResults.innerHTML = markup;
    this.renderRecentSearches();
    this.removeAttribute('results');
    this.setAttribute('suggestions', true);
    this.open();
  }

  renderRecentSearches() {
    const recent = this.predictiveSearchResults.querySelector('.predictive-search__recent');
    if (!recent) return;

    const searches = getRecentSearches();
    const template = recent.querySelector('template').content.firstElementChild;
    const items = searches.map((term, index) => {
      const item = template.cloneNode(true);
      const link = item.querySelector('a');
      item.id = `predictive-search-option-recent-${index + 1}`;
      link.href = `${routes.search_url}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
      item.querySelector('.predictive-search__item-heading').textContent = term;
      return item;
    });

    recent.querySelector('ul').replaceChildren(...items);
    recent.hidden = !items.length;
    recent.querySelector('[data-predictive-search-clear-recent]').addEventListener('click', () => {
      setRecentSearches([]);
      recent.hidden = true;
      this.input.focus();
    });
  }

  // Bolds the typed words in product and category titles, the way Shopify
  // already marks them in query suggestions (styled_text).
  highlightSearchTerm() {
    const words = this.getQuery()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!words.length) return;

    const pattern = new RegExp(`(${words.join('|')})`, 'gi');
    this.predictiveSearchResults.querySelectorAll('.predictive-search__item-heading--highlight').forEach((heading) => {
      const title = heading.firstChild;
      if (title?.nodeType !== Node.TEXT_NODE) return;

      const parts = title.textContent.split(pattern).map((part, index) => {
        if (index % 2 === 0) return document.createTextNode(part);
        const mark = document.createElement('mark');
        mark.className = 'predictive-search__highlight';
        mark.textContent = part;
        return mark;
      });
      title.replaceWith(...parts);
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.removeAttribute('suggestions');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.highlightSearchTerm();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('suggestions');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
